import { Injectable, Inject } from '@angular/core';
import { Http, URLSearchParams } from '@angular/http';
import { Auth } from '@shared/auth';
import { CookieService } from '@shared/cookie';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';

export type TrackingEventName =
  | 'page_view'
  | 'page_performance_timings'
  | 'user_timing'
  | 'user_action';

// Common fields added to all tracking events
export interface TrackingEventData {
  acct: string; // App ID / "account"
  location: string; // Target URL (complete URL at the end of the interaction)
  session_id: string; // Unique ID for the session
  t: string; // Timestamp (Unix)
  user_id?: number; // User ID if the user is logged-in
}

export interface TrackingEvent extends TrackingEventData {
  en: TrackingEventName; // Event category
}

@Injectable()
export class Tracking {
  sessionId: string;
  readonly MAX_GET_LENGTH = 2000;

  constructor(
    private http: Http,
    private auth: Auth,
    private cookies: CookieService,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
  ) {
    let sessionId = this.cookies.get(this.config.sessionCookie);
    if (!sessionId) {
      // Generate a new one if not.
      sessionId = this.generateSessionId();
      this.cookies.put('_tracking_session', sessionId, {
        expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      });
    }
    this.sessionId = sessionId;
  }

  track(eventName: TrackingEventName, eventData: Object): Promise<void> {
    const timestamp = Date.now().toString();

    return new Promise<void>(resolve => {
      (<any>window).requestIdleCallback(() => {
        this.auth
          .switchMap(({ uid }) => {
            const e: TrackingEventData = {
              ...{
                acct: window.location.hostname.replace(/^www/, ''),
                t: timestamp,
                en: eventName,
                location: window.location.href,
                user_id: uid ? uid : undefined,
                session_id: this.sessionId,
              },
              ...eventData,
            };
            // compute rough payload size
            const size = Object.values(e).reduce((acc, value) => {
              acc += value ? String(value).length : 0;
              return acc;
            }, 0);
            if (size > this.MAX_GET_LENGTH) {
              return this.http.post(this.config.trackingEndpoint, e);
            } else {
              return this.http.get(this.config.trackingEndpoint, {
                search: this.serialize(e),
              });
            }
          })
          .mapTo(undefined)
          .toPromise()
          .then(resolve);
      });
    });
  }

  push(e: [TrackingEventName, TrackingEventData]) {
    this.track(e[0], e[1]);
  }

  private generateSessionId(): string {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  private serialize(obj: Object): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams();
    Object.keys(obj).forEach((key: string) => {
      params.set(key, (<any>obj)[key]);
    });
    return params;
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/tracking/tracking.service.ts